import { Calendar, Clock, Loader } from 'lucide-react';
import { useState, useEffect } from 'react';

const UpcomingWebinars = () => {
  const [upcomingWebinars, setUpcomingWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUpcomingWebinars = async () => {
      // Versioning cache to ensure consistency across the platform
      const cachedData = localStorage.getItem("cachedUpcomingWebinars_v5");
      const cacheTimestamp = localStorage.getItem("upcomingWebinarsTimestamp_v5");
      const now = Date.now();

      if (cachedData && cacheTimestamp && now - parseInt(cacheTimestamp) < 3600000) {
        setUpcomingWebinars(JSON.parse(cachedData));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch("https://admin.torchbearer.co.ke/api/training-programs/webinars/upcoming");

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const rawPrograms = data.data || (Array.isArray(data) ? data : []);

        const parsedPrograms = rawPrograms.map(program => {
          let parsedFeatures = [];
          if (program.features) {
            try {
              parsedFeatures = typeof program.features === 'string'
                ? JSON.parse(program.features)
                : Array.isArray(program.features)
                  ? program.features
                  : [];
            } catch (e) {
              console.error(`Failed to parse features for program ${program.id}:`, e);
              parsedFeatures = [];
            }
          }
          return { ...program, features: parsedFeatures };
        });

        setUpcomingWebinars(parsedPrograms);
        localStorage.setItem("cachedUpcomingWebinars_v5", JSON.stringify(parsedPrograms));
        localStorage.setItem("upcomingWebinarsTimestamp_v5", Date.now().toString());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUpcomingWebinars();
  }, []);

  const formatDateRange = (startDate, endDate) => {
    if (!startDate) return 'Date TBA';
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : start;
    const options = { month: 'long', day: 'numeric', year: start.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined };

    if (start.toDateString() === end.toDateString() || !endDate) {
      return start.toLocaleDateString('en-US', options);
    }
    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
      return `${start.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - ${end.getDate()}, ${start.getFullYear()}`;
    }
    return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
  };

  const formatTime = (startTime, endTime) => {
    const formatTimeString = (timeString) => {
      if (!timeString) return '';
      const time = new Date(`2000-01-01T${timeString}`);
      return time.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    };
    const start = formatTimeString(startTime);
    const end = formatTimeString(endTime);
    return (start && end) ? `${start} - ${end}` : (start || end || 'Time TBA');
  };

  if (loading) {
    return (
      <div className="text-center py-20">
        <Loader className="w-10 h-10 animate-spin mx-auto text-amber-600 mb-4" />
        <p className="text-gray-500 font-medium tracking-tight">Syncing upcoming webinars...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-2xl p-10 text-center max-w-md mx-auto my-10">
        <p className="text-red-600 font-bold text-lg mb-2">Sync Error</p>
        <p className="text-red-500 text-sm mb-6">{error}</p>
        <button onClick={() => window.location.reload()} className="bg-red-600 text-white px-8 py-2.5 rounded-xl font-bold hover:bg-red-700 transition-all shadow-lg active:scale-95">Retry</button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-12">
      {upcomingWebinars.length === 0 ? (
        <div className="bg-white rounded-[2rem] shadow-xl p-16 text-center border border-gray-100 max-w-2xl mx-auto">
          <Calendar className="w-16 h-16 text-gray-200 mx-auto mb-6" />
          <h3 className="text-2xl font-black text-gray-900 mb-2">No Webinars Scheduled</h3>
          <p className="text-gray-500 font-medium">We're mapping out our next session. Check back soon!</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 px-4">
          {upcomingWebinars.map((webinar) => (
            <div key={webinar.id} className="bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 overflow-hidden flex flex-col group">
              <div className="relative h-48 w-full overflow-hidden">
                <img src={webinar.image_url || '/images/default-training.png'} alt={webinar.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute top-4 left-4">
                  <span className="bg-amber-600 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider shadow-lg">
                    {webinar.program_type || 'Webinar'}
                  </span>
                </div>
              </div>

              <div className="p-6 flex-grow flex flex-col">
                <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2 h-14 leading-tight group-hover:text-amber-600 transition-colors">
                  {webinar.title}
                </h3>

                {webinar.description ? (
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2 h-10 whitespace-pre-line leading-relaxed">
                    {webinar.description}
                  </p>
                ) : (
                  <div className="h-10 mb-4 bg-gray-50 rounded-lg animate-pulse w-3/4"></div>
                )}

                <div className="space-y-3 mb-6">
                  <div className="flex items-center text-gray-600">
                    <Calendar className="w-4 h-4 mr-3 text-amber-500" />
                    <span className="text-sm font-medium uppercase">{formatDateRange(webinar.start_date, webinar.end_date)}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Clock className="w-4 h-4 mr-3 text-amber-500" />
                    <span className="text-sm font-medium">{formatTime(webinar.start_time, webinar.end_time)}</span>
                  </div>
                  {webinar.features && webinar.features.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {webinar.features.slice(0, 3).map((feature, idx) => (
                        <span key={idx} className="bg-amber-50 text-amber-700 text-[10px] px-2 py-0.5 rounded-md border border-amber-100 font-bold whitespace-pre-line">
                          {feature}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="pt-2">
                    {webinar.early_bird_price && parseFloat(webinar.early_bird_price) > 0 ? (
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-500 line-through">
                          {webinar.currency || 'KES'} {parseFloat(webinar.price).toLocaleString()}
                        </span>
                        <span className="text-lg font-bold text-amber-600">
                          {webinar.currency || 'KES'} {parseFloat(webinar.early_bird_price).toLocaleString()}
                          <span className="text-xs ml-1 font-normal">(Early Bird)</span>
                        </span>
                      </div>
                    ) : (
                      <span className="text-lg font-bold text-amber-600">
                        {webinar.price && parseFloat(webinar.price) > 0 ? `${webinar.currency || 'KES'} ${parseFloat(webinar.price).toLocaleString()}` : 'Free'}
                      </span>
                    )}
                  </div>
                </div>

                <div className="mt-auto">
                  {webinar.registration_link ? (
                    <a href={webinar.registration_link} target="_blank" rel="noopener noreferrer" className="block w-full bg-amber-600 text-white text-center py-3 rounded-xl font-bold hover:bg-amber-700 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5">Register Now</a>
                  ) : (
                    <button className="block w-full bg-gray-900 text-white text-center py-3 rounded-xl font-bold opacity-50 cursor-not-allowed">Enrollment Closed</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UpcomingWebinars;